import { merge } from 'xe-utils'
import { createDiscreteApi } from 'naive-ui'
import type { DialogApi, ModalApi } from 'naive-ui'
import { md5 } from 'js-md5'
import JSEncrypt from 'jsencrypt'
import { v4 } from 'uuid'
import i18n from '@/i18n'

class Utils {
  message = ElMessage
  dialog: DialogApi
  modal: ModalApi

  constructor() {
    const { dialog, modal } = createDiscreteApi(['dialog', 'modal'])
    this.dialog = dialog
    this.modal = modal
  }

  /**
   * @description 滚到到底部
   */
  scrollToBottom(dom: HTMLElement) {
    let scrollHeight = dom.scrollHeight //里面内容的实际高度
    let height = dom.clientHeight //网页可见高度
    let maxScrollTop = scrollHeight - height
    dom.scrollTo({
      top: maxScrollTop > 0 ? maxScrollTop : 0,
      behavior: 'smooth'
    })
  }

  /**
   * @description 下载文件
   * @param {string} url: 下载的文件路径
   * @param {object} init: 一个配置项对象，包括所有对请求的设置。参考文档：https://developer.mozilla.org/zh-CN/docs/Web/API/fetch
   * @param {string} filename: 文件名
   */
  download(url: string, init: RequestInit = {}, filename = '') {
    const defaultInit = {
      method: 'GET',
      headers: {
        responseType: 'blob'
      }
    }
    init = merge(defaultInit, init)
    const blob2json = (blob: Blob) => {
      return new Promise((resolve) => {
        let reader = new FileReader()
        reader.onload = function (event) {
          if (!event.target || !event.target.result) return resolve('')
          let jsonData = JSON.parse(event.target.result as string)
          resolve(jsonData)
        }
        reader.readAsText(blob)
      })
    }
    const downloadLink = document.createElement('a')
    fetch(url, init)
      .then((res) => {
        if (!filename) {
          const contentDisposition = res.headers.get('Content-Disposition')
          if (contentDisposition) {
            // 解析 Content-Disposition 头部中的文件名
            const matches = contentDisposition.match(/filename="?([^"]+)"?/)
            if (matches && matches.length > 1) {
              filename = decodeURIComponent(matches[1])
            }
          }
        }
        return res.blob()
      })
      .then(async (blob) => {
        if (blob.type === 'application/json') {
          const json = await blob2json(blob)
          if (json.code !== undefined) {
            return this.message.error(json.message)
          }
        }
        // 将链接地址字符内容转变成blob地址
        downloadLink.href = URL.createObjectURL(blob)
        downloadLink.download = filename
        document.body.appendChild(downloadLink)
        downloadLink.click()
        downloadLink.remove()
      })
      .catch((e) => {
        console.error(e)
      })
  }

  copyText(text: string) {
    try {
      if (window.isSecureContext) {
        navigator.clipboard.writeText(text)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      this.message.success((i18n.global as any).t('copySuccess'))
    } catch (err) {
      this.message.error((i18n.global as any).t('copyFail'))
    }
  }

  /**
   * @description 返回加密后的字符串
   * @param {string} pwd: 密码
   */
  async encryptPwd(pwd: string) {
    let md5Salt, rsaPublicKey
    try {
      // let res = await webSdk.loginApi.loginKey()
      md5Salt = 'md5Salt'
      rsaPublicKey = 'rsaPublicKey'
    } catch (e) {
      this.message.error('获取基础信息失败，请刷新重试')
      return false
    }

    //md5加密
    let md5Pwd = md5(pwd + md5Salt)

    //RSA加密
    let encrypt = new JSEncrypt()
    encrypt.setPublicKey(rsaPublicKey)
    let rsaPas = encrypt.encrypt(md5Pwd)

    return rsaPas
  }

  /**
   * @description 生成唯一id
   */
  uid() {
    return v4()
  }

  isNullOrUndefined(value: unknown): value is null | undefined {
    return value === null || value === undefined
  }

  /**
   * @description 等同于 Promise.withResolvers()
   */
  withResolvers<T = void>() {
    let resolve: (value: T) => void
    let reject: (reason?: any) => void
    let promise = new Promise<T>((res, rej) => {
      resolve = res
      reject = rej
    })
    return {
      resolve: resolve!,
      reject: reject!,
      promise
    }
  }
}

export default Utils
